import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { Button, Card, CardBody, CardHeader, Flex, Stack, Text, useColorModeValue } from "@chakra-ui/react";

import { buildOwnerDocumentsView } from "../../../utils/ownerDocuments";
import SearchBox from "../../Properties/SearchBox";

import PropertyDocumentsCard from "./PropertyDocumentsCard";

export default function Documents() {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [expanded, setExpanded] = useState(() => new Set());

	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const properties = useMemo(() => (storeProperties || []).filter((property) => !property.archived), [storeProperties]);
	const view = useMemo(() => buildOwnerDocumentsView({ properties, query }), [properties, query]);

	const muted = useColorModeValue("gray.600", "gray.300");

	const isExpanded = useCallback((entry) => expanded.has(entry.propertyId), [expanded]);

	const toggleProperty = (entry) => {
		setExpanded((previous) => {
			const next = new Set(previous);
			if (next.has(entry.propertyId)) next.delete(entry.propertyId);
			else next.add(entry.propertyId);
			return next;
		});
	};

	const allExpanded = view.properties.length > 0 && view.properties.every(isExpanded);

	const toggleAll = () => {
		setExpanded(allExpanded ? new Set() : new Set(view.properties.map((entry) => entry.propertyId)));
	};

	return (
		<Stack spacing={3}>
			<Card>
				<CardHeader>
					<Flex alignItems="center" justifyContent="space-between" gap={4} flexWrap="wrap">
						<Stack spacing={0}>
							<Text fontSize="xl" fontWeight="bold">
								{t("navbarMenu.documents")}
							</Text>
							<Text fontSize="sm" color={muted}>
								{t("documents.summary", {
									properties: view.propertyCount,
									leases: view.currentLeaseCount,
									expiring: view.expiringSoonCount,
								})}
							</Text>
						</Stack>
						<SearchBox
							handleSearch={setQuery}
							searchOptions={[{ label: t("table.address"), description: t("documents.searchByAddress") }]}
						/>
					</Flex>
				</CardHeader>
				<CardBody pt={0}>
					<Flex alignItems="center" gap={4} flexWrap="wrap">
						<Button size="xs" onClick={toggleAll} isDisabled={view.properties.length === 0}>
							{allExpanded ? t("documents.collapseAll") : t("documents.expandAll")}
						</Button>
						{view.hiddenCount > 0 && (
							<Text fontSize="xs" color={muted} ml="auto">
								{t("documents.hiddenBySearch", { count: view.hiddenCount })}
							</Text>
						)}
					</Flex>
				</CardBody>
			</Card>

			{view.properties.map((entry) => (
				<PropertyDocumentsCard
					key={entry.propertyId}
					entry={entry}
					isExpanded={isExpanded(entry)}
					onToggle={() => toggleProperty(entry)}
				/>
			))}

			{view.properties.length === 0 && (
				<Card>
					<CardBody>
						<Text fontSize="sm" color={muted}>
							{view.propertyCount === 0 ? t("documents.noProperties") : t("documents.noMatches")}
						</Text>
					</CardBody>
				</Card>
			)}
		</Stack>
	);
}
